/**
 * Spatial focus navigation for the UPGRADE BAY (docs/12 #2). Pure math —
 * no Pixi — so the movement rules are testable and shared.
 */

/**
 * From the focused position, pick the nearest item in the pressed direction:
 * cost = distance along the axis + a heavy penalty for cross-axis drift, so
 * the cursor moves where the player's eye expects. If nothing lies that way,
 * wrap around console-style: jump to the item furthest in the OPPOSITE
 * direction (the far edge), again preferring minimal drift — ←/→/↑/↓ never
 * dead-end. Returns the new index, or -1 when there is nowhere to go.
 */
export function spatialMove(
  positions: ReadonlyArray<{ x: number; y: number }>,
  selected: number,
  dx: number,
  dy: number,
): number {
  const here = positions[selected];
  if (!here) return -1;
  let best = -1;
  let bestScore = Infinity;
  let wrap = -1;
  let wrapScore = Infinity;
  for (let i = 0; i < positions.length; i++) {
    if (i === selected) continue;
    const p = positions[i]!;
    const ox = p.x - here.x;
    const oy = p.y - here.y;
    const along = dx !== 0 ? ox * dx : oy * dy;
    const cross = dx !== 0 ? Math.abs(oy) : Math.abs(ox);
    if (along > 8) {
      const score = along + cross * 2.5;
      if (score < bestScore) { bestScore = score; best = i; }
    } else if (along < -8) {
      // More negative "along" = further toward the opposite edge = better
      const score = along + cross * 2.5;
      if (score < wrapScore) { wrapScore = score; wrap = i; }
    }
  }
  return best >= 0 ? best : wrap;
}
